import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { praxisPoolApi } from "@/lib/api";
import { useToast } from "@/components/SimpleToast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  MapPinned, Search, Trash2, ArrowLeft, Settings2, Phone, Loader2,
  Sparkles, Star, Moon, CalendarCheck,
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { SpecialtyManagerDialog } from "@/components/praxissuche/SpecialtyManagerDialog";
import { fmtDate } from "@/components/praxissuche/poolConstants";
import { PoolDetailHeader } from "@/components/praxissuche/pool-detail/PoolDetailHeader";
import { PoolDetailFilters } from "@/components/praxissuche/pool-detail/PoolDetailFilters";import { PoolDetailResults } from "@/components/praxissuche/pool-detail/PoolDetailResults";
import { ToMailingDialog } from "@/components/praxissuche/pool-detail/ToMailingDialog";
import { ActivityPanel } from "@/components/common/ActivityPanel";

// Poll a background job until it finishes; calls onUpdate with each snapshot.
// Guards against a "stuck" UI: if the job's heartbeat (updated_at) stops
// advancing for too long (e.g. the backend task died on a reload/redeploy and
// left the job "running" forever), we stop polling and surface an error so the
// dialog closes and the user can retry instead of watching a frozen bar.
async function pollJob(jobId, onUpdate) {
  // Track REAL progress (status/done/progress/found), not the heartbeat's
  // updated_at. The heartbeat keeps updated_at fresh so the job "looks alive",
  // which previously masked a genuine hang (bar frozen for many minutes while
  // no practice actually completes). We now stop when real progress stalls.
  let lastKey = null;
  let lastProgress = Date.now();
  let kind = null;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    let data = null;
    try {
      ({ data } = await praxisPoolApi.getJob(jobId));
    } catch (e) {
      data = null; // transient network error -> fall through to stall check
    }
    if (data) {
      onUpdate(data);
      if (data.status === "done" || data.status === "error") return data;
      kind = data.kind || kind;
      const key = `${data.status}|${data.done || 0}|${data.progress || 0}|${data.found || 0}`;
      if (key !== lastKey) {
        lastKey = key;
        lastProgress = Date.now();
      }
    }
    // No real progress for this long -> treat as stuck. The backend now bounds
    // every practice to <=100s, so healthy runs advance well within this window.
    const stallMs = (kind === "enrich" ? 360 : 480) * 1000;
    if (Date.now() - lastProgress > stallMs) {
      return {
        ...(data || {}),
        status: "error",
        error:
          "Der Vorgang macht seit einiger Zeit keinen Fortschritt mehr und wurde gestoppt (vermutlich durch einen Server-Neustart unterbrochen). Bereits verarbeitete Praxen sind gespeichert.",
      };
    }
    await new Promise((r) => setTimeout(r, 1500));
  }
}

// Enrichment counters we accumulate across (possibly resumed) runs.
const COUNT_KEYS = ["emails_found", "phones_found", "hours_found", "addresses_corrected", "google_completed", "doctolib_found", "websites_disabled"];

// A job that died because the backend process restarted mid-run is a transient
// interruption (not a real failure) -> safe to auto-resume the remaining leads.
function isInterruptionError(err) {
  return typeof err === "string" && /(Neustart|reagiert nicht|unterbroch)/i.test(err);
}

function JobProgress({ job }) {
  if (!job) return null;
  const c = job.counts || {};
  return (
    <div className="space-y-2 py-2" data-testid="job-progress">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground truncate pr-2">{job.label || "Läuft…"}</span>
        <span className="font-semibold tabular-nums" data-testid="job-progress-percent">{job.progress ?? 0}%</span>
      </div>
      <Progress value={job.progress ?? 0} />
      {job.kind === "enrich" && (job.total || 0) > 0 && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground pt-1">
          <span>{job.done ?? 0}/{job.total} Praxen</span>
          {c.emails_found > 0 && <span>✉ {c.emails_found} E-Mails</span>}
          {c.phones_found > 0 && <span>☎ {c.phones_found} Telefon</span>}
          {c.hours_found > 0 && <span>🕐 {c.hours_found} Öffnungszeiten</span>}
          {c.addresses_corrected > 0 && <span>📍 {c.addresses_corrected} Adressen</span>}
          {c.google_completed > 0 && <span>🔎 {c.google_completed} via Google</span>}
        </div>
      )}
      {job.kind === "search" && (job.found || 0) > 0 && (
        <p className="text-xs text-muted-foreground pt-1">{job.found} Treffer gefunden</p>
      )}
    </div>
  );
}

export default function Praxissuche() {
  const toast = useToast();
  const [summary, setSummary] = useState([]);
  const [selected, setSelected] = useState(null); // {specialty_key, display_name_de}
  const [showSpecialties, setShowSpecialties] = useState(false);
  const [nightly, setNightly] = useState(null);
  const [nightlyRunning, setNightlyRunning] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [openLeadId, setOpenLeadId] = useState(null);

  const loadSummary = useCallback(async () => {
    try {
      const res = await praxisPoolApi.summary();
      setSummary(Array.isArray(res.data) ? res.data : []);
    } catch { toast.error("Fachrichtungen konnten nicht geladen werden"); }
  }, [toast]);

  const loadNightly = useCallback(async () => {
    try {
      const res = await praxisPoolApi.nightlyStatus();
      setNightly(res.data && res.data.finished_at ? res.data : null);
    } catch (e) { console.debug("Nightly-Status konnte nicht geladen werden", e); }
  }, []);

  useEffect(() => { loadSummary(); loadNightly(); }, [loadSummary, loadNightly]);

  // Deep-Link: /praxissuche?lead=:id öffnet Fachgebiet + Lead-Dialog direkt
  useEffect(() => {
    const leadId = searchParams.get("lead");
    if (!leadId || selected) return;
    (async () => {
      try {
        const res = await praxisPoolApi.getLead(leadId);
        const lead = res.data;
        const match = summary.find((s) => s.specialty_key === lead.specialty_key);
        setSelected(match || { specialty_key: lead.specialty_key, display_name_de: lead.specialty_key });
        setOpenLeadId(leadId);
      } catch { toast.error("Eintrag konnte nicht geöffnet werden"); }
      finally { searchParams.delete("lead"); setSearchParams(searchParams, { replace: true }); }
    })();
  }, [searchParams, summary, selected, setSearchParams, toast]);

  // Deep-Link: /praxissuche?specialty=:key öffnet die Fachrichtung direkt
  // (z.B. Klick auf das globale Hintergrund-Anreicherungs-Badge).
  useEffect(() => {
    const specKey = searchParams.get("specialty");
    if (!specKey || selected) return;
    const match = summary.find((s) => s.specialty_key === specKey);
    setSelected(match || { specialty_key: specKey, display_name_de: specKey });
    searchParams.delete("specialty");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, summary, selected, setSearchParams]);

  const runNightlyNow = async () => {
    setNightlyRunning(true);
    try {
      await praxisPoolApi.nightlyRun();
      toast.success("Anreicherung im Hintergrund gestartet – du wirst per Team-Chat informiert, sobald sie fertig ist.");
      // Ergebnis kommt asynchron; Status nach kurzer Zeit nachladen.
      setTimeout(() => { loadNightly(); loadSummary(); }, 8000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Konnte den Lauf nicht starten");
    } finally { setTimeout(() => setNightlyRunning(false), 8000); }
  };

  return (
    <div className="space-y-6" data-testid="praxissuche-page">
      <div className="brand-hero rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
            <MapPinned className="h-7 w-7 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Praxissuche</h1>
            <p className="text-muted-foreground mt-1 text-sm sm:text-base max-w-2xl">
              Ein gemeinsamer Adress-Pool je Fachrichtung. Weitere Orte einfach dazusuchen –
              bekannte Praxen werden automatisch übersprungen, neue klar markiert.
            </p>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button variant="outline" onClick={() => setShowSpecialties(true)} data-testid="manage-specialties-btn">
            <Settings2 className="h-4 w-4 mr-2" /> Fachrichtungen
          </Button>
        </div>
      </div>

      {!selected ? (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border bg-muted/40 px-4 py-3"
               data-testid="nightly-status-bar">
            <div className="flex items-center gap-2 text-sm flex-1 min-w-0">
              <Moon className="h-4 w-4 text-[var(--brand-blue)] shrink-0" />
              {nightly ? (
                <span className="text-muted-foreground truncate" data-testid="nightly-status-text">
                  Nächtliche Anreicherung (03:00 Uhr) · zuletzt {fmtDate(nightly.finished_at)}:
                  {" "}<span className="font-medium text-foreground">{nightly.total} Praxen</span>
                  {nightly.counts?.emails_found ? ` · ✉ ${nightly.counts.emails_found}` : ""}
                  {nightly.counts?.phones_found ? ` · ☎ ${nightly.counts.phones_found}` : ""}
                  {nightly.counts?.addresses_corrected ? ` · 📍 ${nightly.counts.addresses_corrected}` : ""}
                </span>
              ) : (
                <span className="text-muted-foreground" data-testid="nightly-status-text">
                  Nächtliche Auto-Anreicherung aktiv (täglich 03:00 Uhr) · noch kein Lauf protokolliert
                </span>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={runNightlyNow} disabled={nightlyRunning}
                    data-testid="nightly-run-btn" className="shrink-0">
              {nightlyRunning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              {nightlyRunning ? "Läuft…" : "Jetzt prüfen"}
            </Button>
          </div>
          <PoolGrid summary={summary} onOpen={setSelected} />
        </>
      ) : (
        <PoolDetail
          specialty={selected}
          openLeadId={openLeadId}
          onLeadOpened={() => setOpenLeadId(null)}
          onBack={() => { setSelected(null); loadSummary(); }}
          onSummaryChanged={loadSummary}
        />
      )}

      <SpecialtyManagerDialog
        open={showSpecialties}
        onOpenChange={setShowSpecialties}
        onChanged={loadSummary}
      />
    </div>
  );
}

function PoolGrid({ summary, onOpen }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="pool-grid">
      {summary.map((s) => (
        <Card key={s.specialty_key}
              className="hover:shadow-lg transition-shadow cursor-pointer border-l-4 border-l-[var(--brand-blue)]"
              onClick={() => onOpen(s)} data-testid={`pool-card-${s.specialty_key}`}>
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between gap-2">
              <CardTitle className="text-base">{s.display_name_de}</CardTitle>
              {s.new_count > 0 && (
                <Badge className="bg-blue-600 hover:bg-blue-600" data-testid={`pool-new-badge-${s.specialty_key}`}>
                  {s.new_count} neu
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="text-3xl font-bold" data-testid={`pool-total-${s.specialty_key}`}>{s.total}</div>
            <div className="text-xs text-muted-foreground">Praxen im Pool</div>
            <div className="flex flex-wrap gap-2 pt-1 text-xs">
              <span className="inline-flex items-center gap-1 text-amber-600"><Phone className="h-3 w-3" />{s.contacted} kontaktiert</span>
              <span className="inline-flex items-center gap-1 text-green-600"><Star className="h-3 w-3" />{s.qualified} qualifiziert</span>
              {s.doctolib > 0 && (
                <span className="inline-flex items-center gap-1 text-sky-600" data-testid={`pool-doctolib-${s.specialty_key}`}><CalendarCheck className="h-3 w-3" />{s.doctolib} Doctolib</span>
              )}
              {s.unenriched > 0 && (
                <span className="inline-flex items-center gap-1 text-violet-600" data-testid={`pool-unenriched-${s.specialty_key}`} title="Praxen mit Website, die noch nicht angereichert wurden"><Sparkles className="h-3 w-3" />{s.unenriched} nicht angereichert</span>
              )}
            </div>
            <div className="text-xs text-muted-foreground pt-1">
              Letzte Suche: {fmtDate(s.last_search_at)}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function PoolDetail({ specialty, onBack, onSummaryChanged, openLeadId, onLeadOpened }) {
  const toast = useToast();
  const sk = specialty.specialty_key;
  const [leads, setLeads] = useState([]);
  const [locations, setLocations] = useState([]);
  const [filters, setFilters] = useState({ status: "all", location: "all", radius: "all", only_new: false, has_email: false, is_customer: "all", has_hours: "all", has_doctolib: false, hide_excluded: true, q: "" });
  const [loading, setLoading] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [enrichJob, setEnrichJob] = useState(null);
  const [enrichHidden, setEnrichHidden] = useState(false);
  const [enrichStatus, setEnrichStatus] = useState({ total: specialty.total ?? 0, unenriched: specialty.unenriched ?? 0 });
  const [showSearch, setShowSearch] = useState(false);
  const [editLead, setEditLead] = useState(null);
  const [lastSummary, setLastSummary] = useState(null);
  const [cleanup, setCleanup] = useState({ open: false, count: 0, deleting: false });
  const [deleteAllState, setDeleteAllState] = useState({ open: false, deleting: false });
  const [view, setView] = useState("table"); // table | map
  const [searchCenters, setSearchCenters] = useState([]);
  const [showToMailing, setShowToMailing] = useState(false);
  const [viewingDeleted, setViewingDeleted] = useState(false);
  const [deleted, setDeleted] = useState([]);
  const [deletedUserFilter, setDeletedUserFilter] = useState("all");
  const [emptyTrashState, setEmptyTrashState] = useState({ open: false, deleting: false });

  // Deep-Link: gezielten Lead-Dialog öffnen
  useEffect(() => {
    if (!openLeadId) return;
    (async () => {
      try {
        const res = await praxisPoolApi.getLead(openLeadId);
        setEditLead(res.data);
      } catch { /* still */ }
      finally { onLeadOpened && onLeadOpened(); }
    })();
  }, [openLeadId, onLeadOpened]);

  const loadLeads = useCallback(async (f) => {
    setLoading(true);
    try {
      const params = { specialty_key: sk };
      if (f.status !== "all") params.status = f.status;
      if (f.location !== "all") params.location = f.location;
      if (f.radius && f.radius !== "all") params.radius = f.radius;
      if (f.only_new) params.only_new = true;
      if (f.has_email) params.has_email = true;
      if (f.has_doctolib) params.has_doctolib = true;
      if (f.hide_excluded && f.status === "all") params.hide_excluded = true;
      if (f.is_customer !== "all") params.is_customer = f.is_customer === "yes";
      if (f.has_hours !== "all") params.has_hours = f.has_hours === "yes";
      if (f.q.trim()) params.q = f.q.trim();
      const res = await praxisPoolApi.getLeads(params);
      setLeads(Array.isArray(res.data) ? res.data : []);
    } catch { toast.error("Einträge konnten nicht geladen werden"); }
    finally { setLoading(false); }
  }, [sk, toast]);

  const loadDeleted = useCallback(async (background = false) => {
    if (!background) setLoading(true);
    try {
      const res = await praxisPoolApi.deletedLeads(sk);
      setDeleted(Array.isArray(res.data) ? res.data : []);
    } catch { if (!background) toast.error("Papierkorb konnte nicht geladen werden"); }
    finally { if (!background) setLoading(false); }
  }, [sk, toast]);

  const loadAux = useCallback(async () => {    try { const r = await praxisPoolApi.locations(sk); setLocations(Array.isArray(r.data) ? r.data : []); } catch (e) { console.debug("Orte konnten nicht geladen werden", e); }
    try {
      const s = await praxisPoolApi.searches(sk);
      const seen = new Set();
      const centers = (Array.isArray(s.data) ? s.data : [])
        .filter((r) => typeof r.start_lat === "number" && typeof r.start_lon === "number")
        .map((r) => ({ lat: r.start_lat, lon: r.start_lon, radius_km: r.radius_km, location: r.start_location }))
        .filter((c) => {
          const k = `${c.lat.toFixed(4)},${c.lon.toFixed(4)},${c.radius_km}`;
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        });
      setSearchCenters(centers);
    } catch (e) { console.debug("Suchmittelpunkte konnten nicht geladen werden", e); }
  }, [sk]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadLeads(filters); loadAux(); loadDeleted(true); }, [sk]);

  const refreshEnrichStatus = useCallback(async () => {
    try {
      const res = await praxisPoolApi.summary();
      const s = (Array.isArray(res.data) ? res.data : []).find((x) => x.specialty_key === sk);
      if (s) setEnrichStatus({ total: s.total ?? 0, unenriched: s.unenriched ?? 0 });
    } catch (e) { console.debug("Anreicherungs-Status konnte nicht geladen werden", e); }
  }, [sk]);

  // Beim Öffnen einer Fachrichtung an einen bereits laufenden Enrich-Job
  // ANDOCKEN (ohne neuen Job zu starten) – so zeigt die Ansicht den Fortschritt
  // eines Laufs, der z.B. in einem anderen Tab oder über das globale Badge
  // gestartet wurde.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await praxisPoolApi.getActiveJob();
        const act = data?.active;
        if (cancelled || !act || act.specialty_key !== sk) return;
        setEnriching(true);
        setEnrichJob({ ...act, kind: "enrich" });
        const final = await pollJob(act.id, (j) => { if (!cancelled) setEnrichJob({ ...j, kind: "enrich" }); });
        if (cancelled) return;
        if (final.status === "done") {
          const r = final.result || {};
          toast.success(`Anreicherung abgeschlossen: ${r.enriched ?? final.done ?? 0} Praxen`);
        }
        await loadLeads(filters);
        await refreshEnrichStatus();
      } catch (e) {
        console.debug("Reattach an laufenden Enrich-Job fehlgeschlagen", e);
      } finally {
        if (!cancelled) { setEnriching(false); setEnrichJob(null); setEnrichHidden(false); }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sk]);

  const applyFilter = (patch) => {
    if (viewingDeleted) setViewingDeleted(false);
    const nf = { ...filters, ...patch };
    setFilters(nf);
    loadLeads(nf);
  };

  const toggleDeletedView = () => {
    const next = !viewingDeleted;
    setViewingDeleted(next);
    if (next) loadDeleted();
    else loadLeads(filters);
  };

  const restoreLead = async (lead) => {
    try {
      await praxisPoolApi.restoreLead(lead.id);
      setDeleted((ls) => ls.filter((l) => l.id !== lead.id));
      toast.success(`„${lead.practice_name}" wiederhergestellt`);
      await loadLeads(filters);
      onSummaryChanged();
    } catch { toast.error("Wiederherstellung fehlgeschlagen"); }
  };

  const doEmptyTrash = async () => {
    setEmptyTrashState((s) => ({ ...s, deleting: true }));
    try {
      const res = await praxisPoolApi.emptyTrash(sk);
      toast.success(`${res.data.deleted} Praxen endgültig gelöscht`);
      setEmptyTrashState({ open: false, deleting: false });
      setDeleted([]);
      onSummaryChanged();
    } catch {
      toast.error("Papierkorb konnte nicht geleert werden");
      setEmptyTrashState((s) => ({ ...s, deleting: false }));
    }
  };

  const stats = {
    total: leads.length,
    neu: leads.filter((l) => l.is_new).length,
    kontaktiert: leads.filter((l) => l.status === "kontaktiert").length,
    qualifiziert: leads.filter((l) => l.status === "qualifiziert").length,
  };

  // Verfügbare Umkreise (km) aus den Suchläufen, absteigend sortiert
  const radii = [...new Set((searchCenters || []).map((c) => c.radius_km).filter((r) => r != null))].sort((a, b) => a - b);

  // Papierkorb: Benutzer, die gelöscht haben + gefilterte Liste
  const deletedUsers = [...new Set((deleted || []).map((l) => l.deleted_by).filter(Boolean))].sort();
  const displayedDeleted = deletedUserFilter === "all"
    ? deleted
    : deleted.filter((l) => l.deleted_by === deletedUserFilter);

  const enrich = async (onlyUnenriched = false) => {
    setEnriching(true);
    // Läufe können durch einen Server-Neustart unterbrochen werden. Bereits
    // angereicherte Praxen sind persistiert (enriched=True) -> wir setzen dann
    // automatisch mit „nur noch nicht angereicherte" fort, bis ein Lauf sauber
    // „Fertig" meldet oder ein echter Fehler auftritt.
    const base = { enriched: 0, emails_found: 0, phones_found: 0, hours_found: 0, addresses_corrected: 0, google_completed: 0, doctolib_found: 0, websites_disabled: 0 };
    const MAX_ATTEMPTS = 8;
    let unenrichedMode = onlyUnenriched;
    let attempt = 0;
    try {
      while (attempt < MAX_ATTEMPTS) {
        attempt += 1;
        setEnrichJob({
          kind: "enrich", status: "running", progress: 0,
          label: attempt > 1 ? `Fortsetzen nach Unterbrechung… (Versuch ${attempt})` : "Wird vorbereitet…",
          done: base.enriched, total: base.enriched, counts: { ...base },
        });
        let data;
        try {
          ({ data } = await praxisPoolApi.enrich(sk, unenrichedMode));
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Anreicherung fehlgeschlagen");
          return;
        }
        const final = await pollJob(data.job_id, (j) => {
          const mergedCounts = { ...(j.counts || {}) };
          for (const k of COUNT_KEYS) mergedCounts[k] = (base[k] || 0) + (j.counts?.[k] || 0);
          setEnrichJob({ ...j, kind: "enrich", counts: mergedCounts, done: base.enriched + (j.done || 0), total: base.enriched + (j.total || 0) });
        });

        // Diesen Lauf in die Gesamtsumme übernehmen.
        const runCounts = final.result || final.counts || {};
        base.enriched += (final.done || final.result?.enriched || 0);
        for (const k of COUNT_KEYS) base[k] = (base[k] || 0) + (runCounts[k] || 0);

        if (final.status === "error") {
          if (isInterruptionError(final.error) && attempt < MAX_ATTEMPTS) {
            unenrichedMode = true; // nur die noch offenen Praxen fortsetzen
            continue;
          }
          toast.error(final.error || "Anreicherung fehlgeschlagen");
          await loadLeads(filters);
          await refreshEnrichStatus();
          return;
        }

        toast.success(`${base.enriched} angereichert · ${base.emails_found} E-Mails · ${base.phones_found} Telefon · ${base.hours_found} Öffnungszeiten · ${base.addresses_corrected} Adressen · ${base.google_completed} via Google${base.doctolib_found ? ` · ${base.doctolib_found} Doctolib-Terminkalender` : ""}${base.websites_disabled ? ` · ${base.websites_disabled} Website(s) deaktiviert` : ""}${attempt > 1 ? ` · fortgesetzt in ${attempt} Läufen` : ""}`);
        await loadLeads(filters);
        await refreshEnrichStatus();
        return;
      }
      toast.error("Die Anreicherung wurde mehrfach unterbrochen. Bereits angereicherte Praxen sind gespeichert – bitte später erneut „Nur noch nicht angereicherte“ starten.");
      await loadLeads(filters);
      await refreshEnrichStatus();
    } finally { setEnriching(false); setEnrichJob(null); setEnrichHidden(false); }
  };

  const markSeen = async () => {
    try {
      const res = await praxisPoolApi.markSeen(sk);
      toast.success(`${res.data.updated} als gesehen markiert`);
      await loadLeads(filters);
      onSummaryChanged();
    } catch { toast.error("Aktion fehlgeschlagen"); }
  };

  const changeStatus = async (lead, status) => {
    try {
      const res = await praxisPoolApi.updateLead(lead.id, { status });
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? res.data : l)));
      onSummaryChanged();
    } catch { toast.error("Status konnte nicht geändert werden"); }
  };

  const convertLead = async (lead) => {
    try {
      const res = await praxisPoolApi.convertCustomer(lead.id);
      toast.success(res.data.already ? "Bereits als Kunde vorhanden" : "Als Interessent übernommen");
      setLeads((ls) => ls.map((l) => (l.id === lead.id ? { ...l, converted_customer_id: res.data.customer_id, status: "qualifiziert" } : l)));
      onSummaryChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Übernahme fehlgeschlagen"); }
  };

  const saveEdit = async (leadId, data, expected) => {
    const res = await praxisPoolApi.updateLead(leadId, data, expected);
    setLeads((ls) => ls.map((l) => (l.id === leadId ? res.data : l)));
    return res.data;
  };

  const removeLead = async (lead) => {    try {
      await praxisPoolApi.deleteLead(lead.id);
      setLeads((ls) => ls.filter((l) => l.id !== lead.id));
      loadDeleted(true);
      onSummaryChanged();
    } catch { toast.error("Eintrag konnte nicht gelöscht werden"); }
  };

  const openCleanup = async () => {
    try {
      const res = await praxisPoolApi.incompleteCount(sk);
      setCleanup({ open: true, count: res.data.count, deleting: false });
    } catch { toast.error("Anzahl konnte nicht ermittelt werden"); }
  };

  const doCleanup = async () => {
    setCleanup((c) => ({ ...c, deleting: true }));
    try {
      const res = await praxisPoolApi.deleteIncomplete(sk);
      toast.success(`${res.data.deleted} unvollständige Praxen gelöscht`);
      setCleanup({ open: false, count: 0, deleting: false });
      await loadLeads(filters);
      onSummaryChanged();
    } catch {
      toast.error("Löschen fehlgeschlagen");
      setCleanup((c) => ({ ...c, deleting: false }));
    }
  };

  const doDeleteAll = async () => {
    setDeleteAllState((s) => ({ ...s, deleting: true }));
    try {
      const res = await praxisPoolApi.deleteAll(sk);
      toast.success(`${res.data.deleted} Praxen gelöscht`);
      setDeleteAllState({ open: false, deleting: false });
      await loadLeads(filters);
      onSummaryChanged();
    } catch {
      toast.error("Löschen fehlgeschlagen");
      setDeleteAllState((s) => ({ ...s, deleting: false }));
    }
  };

  const exportParams = () => {
    const p = { specialty_key: sk };
    if (filters.status !== "all") p.status = filters.status;
    if (filters.location !== "all") p.location = filters.location;
    if (filters.radius && filters.radius !== "all") p.radius = filters.radius;
    if (filters.only_new) p.only_new = "true";
    if (filters.hide_excluded && filters.status === "all") p.hide_excluded = "true";
    return p;
  };
  const download = (url, filename) => {
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <div className="space-y-4" data-testid="pool-detail">
      <Button variant="ghost" onClick={onBack} data-testid="pool-back-btn">
        <ArrowLeft className="h-4 w-4 mr-2" /> Zurück zur Übersicht
      </Button>

      {enrichJob && enrichHidden && (
        <button
          type="button"
          onClick={() => setEnrichHidden(false)}
          data-testid="enrich-bg-badge"
          className="flex w-full items-center gap-3 rounded-lg border border-violet-200 dark:border-violet-900 bg-violet-50 dark:bg-violet-950/30 px-4 py-2.5 text-left text-sm text-violet-800 dark:text-violet-200 transition-colors hover:bg-violet-100 dark:hover:bg-violet-950/50"
        >
          <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
          <span className="flex-1 min-w-0 truncate">
            Anreicherung läuft im Hintergrund{typeof enrichJob.total === "number" && enrichJob.total > 0 ? ` · ${enrichJob.done ?? 0} von ${enrichJob.total}` : ""}…
          </span>
          <span className="shrink-0 font-medium underline underline-offset-2">Fortschritt anzeigen</span>
        </button>
      )}

      <PoolDetailHeader
        specialty={specialty}
        stats={stats}
        lastSummary={lastSummary}
        enriching={enriching}
        enrichStatus={enrichStatus}
        onEnrichRest={() => enrich(true)}
        onSearch={() => setShowSearch(true)}
        onEnrich={enrich}
        onMarkSeen={markSeen}
        onOpenCleanup={openCleanup}
        onDeleteAll={() => setDeleteAllState({ open: true, deleting: false })}
        onExportCsv={() => download(praxisPoolApi.exportCsvUrl(exportParams()), `pool_${sk}.csv`)}
        onExportXlsx={() => download(praxisPoolApi.exportXlsxUrl(exportParams()), `pool_${sk}.xlsx`)}
        onToMailing={() => setShowToMailing(true)}
      />

      <PoolDetailFilters
        filters={filters}
        setFilters={setFilters}
        applyFilter={applyFilter}
        onSearchEnter={loadLeads}
        locations={locations}
        radii={radii}
        view={view}
        setView={setView}
        viewingDeleted={viewingDeleted}
        deletedCount={deleted.length}
        onToggleDeleted={toggleDeletedView}
        onEmptyTrash={() => setEmptyTrashState({ open: true, deleting: false })}
        deletedUsers={deletedUsers}
        deletedUserFilter={deletedUserFilter}
        onDeletedUserFilter={setDeletedUserFilter}
      />

      <PoolDetailResults
        loading={loading}
        view={view}
        leads={viewingDeleted ? displayedDeleted : leads}
        searchCenters={searchCenters}
        onChangeStatus={changeStatus}
        onConvert={convertLead}
        onRemove={removeLead}
        onEdit={setEditLead}
        deletedMode={viewingDeleted}
        onRestore={restoreLead}
      />

      <PoolSearchDialog
        open={showSearch}
        onOpenChange={setShowSearch}
        specialtyKey={sk}
        onDone={async (res) => {
          setShowSearch(false);
          setLastSummary(res);
          toast.success(`${res.added_new} neu · ${res.skipped_existing} bereits vorhanden`);
          await loadLeads(filters);
          await loadAux();
          onSummaryChanged();
        }}
      />

      <EditLeadDialog
        lead={editLead}
        onOpenChange={(o) => !o && setEditLead(null)}
        onSave={saveEdit}
      />

      <ToMailingDialog
        open={showToMailing}
        onOpenChange={setShowToMailing}
        specialtyKey={sk}
      />

      <Dialog open={cleanup.open} onOpenChange={(o) => !o && setCleanup((c) => ({ ...c, open: false }))}>
        <DialogContent className="max-w-md" data-testid="cleanup-dialog">
          <DialogHeader>
            <DialogTitle>Unvollständige Praxen entfernen</DialogTitle>
            <DialogDescription>
              Es werden Praxen gelöscht, bei denen <strong>Straße oder Ort fehlt</strong> und die
              <strong> keine Internetseite</strong> haben (können nicht mehr angereichert werden).
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 text-sm">
            {cleanup.count > 0 ? (
              <p data-testid="cleanup-count">
                In dieser Fachrichtung werden <strong className="text-red-600">{cleanup.count}</strong> Praxen
                unwiderruflich gelöscht.
              </p>
            ) : (
              <p className="text-muted-foreground" data-testid="cleanup-count">Keine unvollständigen Praxen gefunden.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCleanup((c) => ({ ...c, open: false }))}>Abbrechen</Button>
            <Button variant="destructive" onClick={doCleanup} disabled={cleanup.deleting || cleanup.count === 0} data-testid="cleanup-confirm-btn">
              {cleanup.deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              {cleanup.count} Praxen löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteAllState.open} onOpenChange={(o) => !o && setDeleteAllState((s) => ({ ...s, open: false }))}>
        <DialogContent className="max-w-md" data-testid="delete-all-dialog">
          <DialogHeader>
            <DialogTitle>Alle Praxen löschen</DialogTitle>
            <DialogDescription>
              Sind Sie sich sicher? Es werden <strong>alle Praxen</strong> in der Fachrichtung
              <strong> {specialty.display_name_de}</strong> unwiderruflich gelöscht.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteAllState((s) => ({ ...s, open: false }))}>Abbrechen</Button>
            <Button variant="destructive" onClick={doDeleteAll} disabled={deleteAllState.deleting} data-testid="delete-all-confirm-btn">
              {deleteAllState.deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Ja, alle löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={emptyTrashState.open} onOpenChange={(o) => !o && setEmptyTrashState((s) => ({ ...s, open: false }))}>
        <DialogContent className="max-w-md" data-testid="empty-trash-dialog">
          <DialogHeader>
            <DialogTitle>Papierkorb leeren</DialogTitle>
            <DialogDescription>
              Alle <strong>{deleted.length}</strong> gelöschten Praxen in der Fachrichtung
              <strong> {specialty.display_name_de}</strong> werden <strong>unwiderruflich</strong> entfernt
              und können nicht mehr wiederhergestellt werden.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmptyTrashState((s) => ({ ...s, open: false }))}>Abbrechen</Button>
            <Button variant="destructive" onClick={doEmptyTrash} disabled={emptyTrashState.deleting || deleted.length === 0} data-testid="empty-trash-confirm-btn">
              {emptyTrashState.deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Endgültig löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!enrichJob && !enrichHidden} onOpenChange={(o) => { if (!o) setEnrichHidden(true); }}>
        <DialogContent className="max-w-md" data-testid="enrich-progress-dialog">
          <DialogHeader>
            <DialogTitle>Daten werden angereichert…</DialogTitle>
            <DialogDescription>
              Telefon, E-Mail, Öffnungszeiten und Anschrift werden aus den Websites/Impressen ausgelesen.
            </DialogDescription>
          </DialogHeader>
          <JobProgress job={enrichJob} />
          <div className="rounded-lg bg-muted/60 px-3 py-2 text-xs text-muted-foreground" data-testid="enrich-background-hint">
            Der Vorgang läuft im Hintergrund auf dem Server weiter – Sie können dieses Fenster schließen
            oder die Seite verlassen. Bei größeren Läufen werden Sie im Team-Chat benachrichtigt, sobald es fertig ist.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEnrichHidden(true)} data-testid="enrich-hide-btn">
              Im Hintergrund weiterlaufen lassen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EditLeadDialog({ lead, onOpenChange, onSave }) {
  const toast = useToast();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (lead) {
      setForm({
        practice_name: lead.practice_name || "",
        person_name: lead.person_name || "",
        street: lead.street || "",
        zip: lead.zip || "",
        city: lead.city || "",
        phone: lead.phone || "",
        email: lead.email || "",
        website: lead.website || "",
        opening_hours: lead.opening_hours || "",
        notes: lead.notes || "",
      });
    }
  }, [lead]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.practice_name.trim()) return toast.error("Praxisname darf nicht leer sein");
    setSaving(true);
    try {
      await onSave(lead.id, form, lead.last_edited_at);
      toast.success("Eintrag gespeichert");
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!lead} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-lead-dialog">
        <DialogHeader>
          <DialogTitle>Eintrag bearbeiten</DialogTitle>
          <DialogDescription>Praxis- und Kontaktdaten anpassen. Änderungen werden protokolliert.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {(lead?.source_location || lead?.source_radius_km != null || lead?.distance_km != null) && (
            <div className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="edit-lead-origin">
              <span className="font-medium text-foreground">Herkunft:</span>
              {lead?.source_location && <span>{lead.source_location}</span>}
              {lead?.source_radius_km != null && (
                <span className="inline-flex items-center rounded-full border px-2 py-0.5" data-testid="edit-lead-radius">Umkreis {lead.source_radius_km} km</span>
              )}
              {lead?.distance_km != null && (
                <span data-testid="edit-lead-distance">{lead.distance_km} km entfernt</span>
              )}
            </div>
          )}
          <div>
            <Label>Praxisname</Label>
            <Input value={form.practice_name || ""} onChange={upd("practice_name")} data-testid="edit-practice-name" />
          </div>
          <div>
            <Label>Ansprechpartner</Label>
            <Input value={form.person_name || ""} onChange={upd("person_name")} data-testid="edit-person-name" />
          </div>
          <div>
            <Label>Straße</Label>
            <Input value={form.street || ""} onChange={upd("street")} data-testid="edit-street" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>PLZ</Label>
              <Input value={form.zip || ""} onChange={upd("zip")} data-testid="edit-zip" />
            </div>
            <div className="col-span-2">
              <Label>Ort</Label>
              <Input value={form.city || ""} onChange={upd("city")} data-testid="edit-city" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Telefon</Label>
              <Input value={form.phone || ""} onChange={upd("phone")} data-testid="edit-phone" />
            </div>
            <div>
              <Label>E-Mail</Label>
              <Input value={form.email || ""} onChange={upd("email")} data-testid="edit-email" />
            </div>
          </div>
          <div>
            <Label>Website</Label>
            <Input value={form.website || ""} onChange={upd("website")} data-testid="edit-website" />
          </div>
          <div>
            <Label>Öffnungszeiten</Label>
            <Textarea value={form.opening_hours || ""} onChange={upd("opening_hours")} rows={2} placeholder="z. B. Mo–Fr 8:00 – 18:00" data-testid="edit-opening-hours" />
          </div>
          <div>
            <Label>Notizen</Label>
            <Textarea value={form.notes || ""} onChange={upd("notes")} rows={3} data-testid="edit-notes" />
          </div>
        </div>
        {lead?.id && (
          <div className="mt-4 border-t border-border pt-4">
            <ActivityPanel entityType="lead" entityId={lead.id} />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={submit} disabled={saving} data-testid="edit-lead-save">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PoolSearchDialog({ open, onOpenChange, specialtyKey, onDone }) {
  const toast = useToast();
  const [form, setForm] = useState({
    start_location: "63073 Offenbach am Main",
    start_lat: "", start_lon: "", radius_km: 30, target: "",
    sources: ["overpass", "google"],
  });
  const [saving, setSaving] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [job, setJob] = useState(null);

  useEffect(() => { if (open) { setForm((f) => ({ ...f, target: "", start_lat: "", start_lon: "" })); setJob(null); } }, [open]);

  const toggleSource = (src) => setForm((f) => ({
    ...f, sources: f.sources.includes(src) ? f.sources.filter((s) => s !== src) : [...f.sources, src],
  }));

  const doGeocode = async () => {
    if (!form.start_location.trim()) return toast.error("Bitte zuerst Startort angeben");
    setGeocoding(true);
    try {
      const res = await praxisPoolApi.geocode(form.start_location.trim());
      setForm((f) => ({ ...f, start_lat: res.data.lat, start_lon: res.data.lon }));
      toast.success(`Koordinaten: ${res.data.lat.toFixed(4)}, ${res.data.lon.toFixed(4)}`);
    } catch { toast.error("Geocoding fehlgeschlagen – bitte Koordinaten manuell eintragen."); }
    finally { setGeocoding(false); }
  };

  const submit = async () => {
    if (!form.start_location.trim()) return toast.error("Bitte Startort angeben");
    if (!form.sources.length) return toast.error("Bitte mindestens eine Quelle wählen");
    setSaving(true);
    setJob({ kind: "search", status: "running", progress: 0, label: "Wird vorbereitet…", found: 0 });
    try {
      const payload = {
        specialty_key: specialtyKey,
        start_location: form.start_location.trim(),
        radius_km: Number(form.radius_km),
        sources: form.sources,
      };
      if (form.target !== "") payload.target = parseInt(form.target, 10);
      if (form.start_lat !== "" && form.start_lon !== "") {
        payload.start_lat = Number(form.start_lat);
        payload.start_lon = Number(form.start_lon);
      }
      const { data } = await praxisPoolApi.search(payload);
      const final = await pollJob(data.job_id, (j) => setJob({ ...j, kind: "search" }));
      if (final.status === "error") {
        toast.error(final.error || "Suche fehlgeschlagen");
        setJob(null);
        return;
      }
      onDone(final.result);
    } catch (e) { toast.error(e?.response?.data?.detail || "Suche fehlgeschlagen"); setJob(null); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="pool-search-dialog">
        <DialogHeader>
          <DialogTitle>Adressen suchen &amp; hinzufügen</DialogTitle>
          <DialogDescription>
            Neue Praxen dieser Fachrichtung im gewählten Gebiet suchen. Bereits im Pool vorhandene
            Praxen werden automatisch übersprungen.
          </DialogDescription>
        </DialogHeader>
        {saving ? (
          <div className="py-2">
            <JobProgress job={job} />
            <p className="text-xs text-muted-foreground pt-3">
              Bitte warten – die Suche kann je nach Gebiet einige Sekunden dauern. Dieses Fenster kann geöffnet bleiben.
            </p>
          </div>
        ) : (
        <div className="space-y-4">
          <div>
            <Label>Ort / PLZ</Label>
            <div className="flex gap-2">
              <Input value={form.start_location} onChange={(e) => setForm({ ...form, start_location: e.target.value })}
                     data-testid="search-location" placeholder="z. B. 60311 Frankfurt am Main" />
              <Button type="button" variant="outline" onClick={doGeocode} disabled={geocoding} data-testid="search-geocode-btn" className="shrink-0">
                {geocoding ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPinned className="h-4 w-4" />}
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Latitude (optional)</Label>
              <Input type="number" value={form.start_lat} onChange={(e) => setForm({ ...form, start_lat: e.target.value })} data-testid="search-lat" placeholder="50.1086" />
            </div>
            <div>
              <Label className="text-xs">Longitude (optional)</Label>
              <Input type="number" value={form.start_lon} onChange={(e) => setForm({ ...form, start_lon: e.target.value })} data-testid="search-lon" placeholder="8.7761" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Radius (km)</Label>
              <Input type="number" value={form.radius_km} onChange={(e) => setForm({ ...form, radius_km: e.target.value })} data-testid="search-radius" />
            </div>
            <div>
              <Label>Max. neue (optional)</Label>
              <Input type="number" min="1" value={form.target} onChange={(e) => setForm({ ...form, target: e.target.value })} data-testid="search-target" placeholder="alle" />
              <p className="text-xs text-muted-foreground mt-1">Leer = alle Neuen hinzufügen.</p>
            </div>
          </div>
          <div>
            <Label>Datenquellen</Label>
            <div className="flex flex-col gap-2 mt-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={form.sources.includes("overpass")} onCheckedChange={() => toggleSource("overpass")} data-testid="source-overpass" />
                OpenStreetMap (kostenlos, sofort)
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={form.sources.includes("google")} onCheckedChange={() => toggleSource("google")} data-testid="source-google" />
                Google Places (bessere Kontaktdaten, Bewertungen)
              </label>
            </div>
          </div>
        </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Abbrechen</Button>
          <Button onClick={submit} disabled={saving} data-testid="search-submit">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
            {saving ? "Suche läuft…" : "Suchen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

